import { Box, Button, Popup, Table, TableRow, TopBar } from '@sam/components';
import { TableHeader, TableKey } from '@sam/components/src/Table/Table.types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  InvoiceState,
  Receipt,
  ReceiptType,
  addOrRemove,
  getAllReceiptsByTypeAndState,
  getInvoicePDFDocument,
  updateSchambeckUserTableConfig,
  useData,
} from 'shared';
import { useUser } from '../../components/UserContext';
import { convertInvoicesIntoTableRows } from '../../utils/invoice/Invoice.utils';

export const InvoiceOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { axios, user, updateContextUser, selectedOffices } = useUser();
  const [invoices, setInvoices] = useState<Receipt[]>([]);
  const [selectedStates, setSelectedStates] = useState<InvoiceState[]>([]);
  const [selectedReceiptTypes, setSelectedReceiptTypes] = useState<ReceiptType[]>([
    ReceiptType.INVOICE,
  ]);
  const [filterValue, setFilterValue] = useState<string>('');
  const [documentToShow, setDocumentToShow] = useState<string>();

  const { data: allOrders } = useData('ORDER_ALL', {
    config: { fallbackData: [] },
  });
  const { data: allProjects } = useData('PROJECT_ALL', {
    config: { fallbackData: [] },
  });
  const { data: allCustomers } = useData('CUSTOMER_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: allCustomerLocations } = useData('CUSTOMER_LOCATION_ALL', {
    config: { fallbackData: [] },
  });
  const { data: allCurrencies } = useData('CURRENCY_ALL', {
    config: { fallbackData: [] },
  });
  const { data: allCompanies } = useData('COMPANY_ALL', {
    config: { fallbackData: [] },
  });
  /**
   * helper method to get the preview of a receipt
   * @param invoiceId id of the receipt to load
   */
  const loadReceiptDocument = useCallback(
    (invoiceId: string): void => {
      getInvoicePDFDocument(axios, invoiceId, user.id).then(
        (document) => document && setDocumentToShow(URL.createObjectURL(document)),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user.id],
  );

  /**
   * TableRows out of the loaded invoices and filtered by the selected state
   */
  const rows: TableRow[] = useMemo(
    (): TableRow[] =>
      convertInvoicesIntoTableRows(
        invoices,
        allOrders,
        allProjects,
        allCustomers,
        allCustomerLocations,
        navigate,
        allCurrencies,
        allCompanies,
        loadReceiptDocument,
      ),
    [
      allCompanies,
      allCurrencies,
      allCustomerLocations,
      allCustomers,
      allOrders,
      allProjects,
      invoices,
      loadReceiptDocument,
      navigate,
    ],
  );

  // Hook to load the invoices for the selected state
  useEffect(() => {
    const typesToLoad: ReceiptType[] = [];
    const statesToLoad: InvoiceState[] = [];

    if (selectedReceiptTypes.length === 0) typesToLoad.push(...Object.values(ReceiptType));
    else typesToLoad.push(...selectedReceiptTypes);

    if (selectedStates.length === 0) statesToLoad.push(...Object.values(InvoiceState));
    else statesToLoad.push(...selectedStates);

    getAllReceiptsByTypeAndState(axios, selectedReceiptTypes, selectedStates).then(setInvoices);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStates, selectedReceiptTypes, selectedOffices]);

  //Hook to check if a filter for the current page is present
  useEffect(() => {
    const existingFilter: { path: string; states: InvoiceState[]; types: ReceiptType[] } | false =
      JSON.parse(localStorage.getItem('buttonFilter') || 'false');
    if (!existingFilter) return;
    if (!existingFilter?.path || existingFilter.path !== '/invoice')
      localStorage.removeItem('buttonFilter');
    else {
      setSelectedReceiptTypes(existingFilter.types);
      setSelectedStates(existingFilter?.states);
    }
  }, []);

  /**
   * Updates the selected config and add it to the localSorage
   * @param type the selected receiptType
   */
  const handleSelectReceiptType = (type: ReceiptType): void => {
    const existingFilter:
      | {
          path: string;
          states: InvoiceState[];
          types: ReceiptType[];
        }
      | false = JSON.parse(localStorage.getItem('buttonFilter') || 'false');
    const newStates: InvoiceState[] =
      existingFilter && existingFilter.states ? existingFilter.states : selectedStates;
    localStorage.setItem(
      'buttonFilter',
      JSON.stringify({
        path: '/invoice',
        states: newStates,
        types: addOrRemove(selectedReceiptTypes, type),
      }),
    );
    setSelectedReceiptTypes(addOrRemove(selectedReceiptTypes, type));
  };

  /**
   * Updates the selected config and add it to the localSorage
   * @param state the selected invoiceState
   */
  const handleReceiptStateChange = (state: InvoiceState): void => {
    const existingFilter:
      | {
          path: string;
          states: InvoiceState[];
          types: ReceiptType[];
        }
      | false = JSON.parse(localStorage.getItem('buttonFilter') || 'false');
    const newTypes: ReceiptType[] =
      existingFilter && existingFilter.types ? existingFilter.types : selectedReceiptTypes;
    localStorage.setItem(
      'buttonFilter',
      JSON.stringify({
        path: '/invoice',
        states: addOrRemove(selectedStates, state),
        types: newTypes,
      }),
    );
    setSelectedStates(addOrRemove(selectedStates, state));
  };
  return (
    <>
      <Popup isOpen={!!documentToShow} onClose={() => setDocumentToShow(undefined)}>
        <object data={documentToShow} width="1000px" height="700px" />
      </Popup>
      <TopBar title={t('pages.invoice.overview.topBarHeadline')} onSearch={setFilterValue} />
      <Box>
        <div className="receipt-overview__filter-box">
          <p className="receipt-overview__filter-box__label">{t('pages.invoice.overview.type')}</p>
          <div className="three-columns">
            {Object.values(ReceiptType).map((type) => (
              <Button
                value={t(`general.receiptType.${type}`)}
                active={selectedReceiptTypes.includes(type)}
                onClick={() => handleSelectReceiptType(type)}
              />
            ))}
          </div>

          <p className="receipt-overview__filter-box__label">{t('pages.invoice.overview.state')}</p>
          <div className="three-columns">
            {Object.values(InvoiceState).map((state) => (
              <Button
                value={t(`general.invoiceState.${state}`)}
                active={selectedStates.includes(state)}
                onClick={() => handleReceiptStateChange(state)}
              />
            ))}
          </div>
        </div>
      </Box>
      <Box>
        <Table
          tableConfig={user.tableConfig?.get(TableKey.INVOICE_OVERVIEW)}
          updateTableConfig={(updatedHeader) => {
            updateSchambeckUserTableConfig(
              axios,
              user,
              TableKey.INVOICE_OVERVIEW,
              updatedHeader,
            ).then((updatedUser) => updatedUser && updateContextUser(updatedUser));
          }}
          filterValue={filterValue}
          rows={rows}
          header={
            t('pages.invoice.overview.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </>
  );
};
